import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, STATUS_LABELS, STATUS_CLASS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Warning, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function CreditAnalysis() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [rent, setRent] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/credit/analyses").then((r) => setHistory(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId || !rent) { toast.error("Selecione cliente e informe o aluguel"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/credit/analyze", { client_id: clientId, rent_amount: parseFloat(rent) });
      setResult(data);
      toast.success("Análise concluída");
      const h = await api.get("/credit/analyses");
      setHistory(h.data);
    } catch { toast.error("Erro na análise"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-8">
        <div className="eyebrow mb-1">Módulo de Crédito</div>
        <h1 className="font-display text-4xl tracking-tight">Análise de Crédito</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Consulta avulsa para clientes já cadastrados. Para gerar contrato completo, use o <Link to="/garantia" className="text-[var(--emerald)] underline">wizard de Garantia Locatícia</Link>.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <form onSubmit={submit} className="card-flat p-6 lg:col-span-2" data-testid="credit-form">
          <div className="eyebrow mb-4">Consulta</div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Cliente</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="credit-client"><SelectValue placeholder="Escolha o cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name} — {c.cpf}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Valor do aluguel</Label>
              <Input data-testid="credit-rent" type="number" value={rent} onChange={(e) => setRent(e.target.value)} placeholder="2500" />
            </div>
            <Button data-testid="credit-submit" type="submit" disabled={loading} className="btn-primary w-full h-11">
              <MagnifyingGlass size={16} className="mr-2" /> {loading ? "Consultando…" : "Analisar crédito"}
            </Button>
            <p className="text-[11px] text-slate-400 leading-relaxed">Provedor: MOCK (adaptador pronto para SPC / Serasa / Boa Vista).</p>
          </div>
        </form>

        <div className="lg:col-span-3 space-y-4">
          {result && (
            <div className="card-flat p-6" data-testid="credit-result">
              <div className="flex items-start justify-between">
                <div>
                  <div className="eyebrow">Resultado</div>
                  <div className="font-display text-2xl mt-1">{result.name}</div>
                  <div className="text-xs text-slate-500 mono">{result.cpf}</div>
                </div>
                <span className={`status-pill ${STATUS_CLASS[result.status]}`}>{STATUS_LABELS[result.status]}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Box label="Score" value={result.score} tone={result.score >= 650 ? "success" : result.score >= 500 ? "warn" : "danger"} />
                <Box label="Multiplicador" value={`${result.multiplier}x`} />
                <Box label="Limite" value={fmtBRL(result.approved_limit)} />
              </div>
              {result.restrictions?.length > 0 && (
                <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-800 flex gap-2">
                  <Warning size={14} /> {result.restrictions.length} restrição(ões) SPC encontrada(s)
                </div>
              )}
              {result.status !== "reprovado" && (
                <Link to="/garantia"><Button className="btn-primary mt-4"><ShieldCheck size={14} className="mr-2" /> Gerar Contrato</Button></Link>
              )}
            </div>
          )}

          <div className="card-flat p-6" data-testid="credit-history">
            <div className="eyebrow mb-3">Histórico</div>
            {history.length === 0 ? (
              <div className="text-sm text-slate-400 py-4">Sem consultas ainda.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-slate-500 eyebrow">
                  <tr><th className="text-left py-2">Nome</th><th className="text-left py-2">CPF</th><th className="text-left py-2">Score</th><th className="text-left py-2">Status</th><th className="text-left py-2">Data</th></tr>
                </thead>
                <tbody>
                  {history.slice(0, 10).map((h) => (
                    <tr key={h.analysis_id} className="border-t border-[var(--line)]">
                      <td className="py-2 font-medium">{h.name}</td>
                      <td className="py-2 mono text-slate-500">{h.cpf}</td>
                      <td className="py-2 mono">{h.score}</td>
                      <td className="py-2"><span className={`status-pill ${STATUS_CLASS[h.status]}`}>{STATUS_LABELS[h.status]}</span></td>
                      <td className="py-2 text-slate-500">{fmtDate(h.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
function Box({ label, value, tone }) {
  const color = tone === "danger" ? "#DC2626" : tone === "warn" ? "#D97706" : tone === "success" ? "#059669" : "#0F172A";
  return <div className="p-3 rounded-md bg-slate-50 border border-slate-100"><div className="eyebrow">{label}</div><div className="metric-value text-xl mt-1" style={{color}}>{value}</div></div>;
}
